<script setup lang="ts">
import { computed, ref } from "vue";
import { ClaimType, EffectiveFlight as Flight, ReroutingProposals, EffectiveAirline } from '@/interfaces';
import FlightOverviewHeader from "./FlightOverviewHeader.vue";
import FlightOverviewItinerary from "./FlightOverviewItinerary.vue";
import FlightOverviewSchedule from "./FlightOverviewSchedule.vue";

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon } from '@heroicons/vue/20/solid'

const props = defineProps<{
  airline: EffectiveAirline;
  flight: Flight;
  claimTypeName: ClaimType | null;
  reroutingProposals: ReroutingProposals;
  actualRerouting: any;
  cancelledBy?: string | null;
  cancelledIndicator?: "RECORD" | "SEGMENT";
  cancelledSignature?: string;
  cancelledDate?: Date;
}>();

// @TODO: Display Squeleton or Loader if flight is null
const claimTypeName = computed(() => props.claimTypeName);
const flight = computed(() => props.flight);

const displayUTC = ref(false);
</script>

<template>
  <div class="flex flex-col py-2 mx-2 my-4 border rounded-md shadow">
    <div class="overflow-hidden text-left bg-white border-b-2">
      <FlightOverviewHeader 
        :flight-id="flight.id"
        :airline-id="airline.legalData?.airlineId || flight.airlineId"
        :airline-company-id="airline.legalData?.companyId"
        :airlineIconURL="airline.legalData?.iconURL"
        :claimTypeName="claimTypeName"
        :flightIdentifier="flight.flightIdentifier"
        :airlineName="airline.name"
        :airlineIcao="flight.airline.icao"
        :cancelledBy="cancelledBy"
        :cancelledIndicator="cancelledIndicator"
        :cancelledSignature="cancelledSignature"
        :cancelledDate="cancelledDate"
      />
    </div>

    <div>
      <FlightOverviewItinerary
        :claimTypeName="claimTypeName"
        :scheduledDepartureUTC="new Date(flight.scheduledDepartureUTC)"
        :scheduledArrivalUTC="new Date(flight.scheduledArrivalUTC)"
        :departureAirport="flight.departureAirport"
        :arrivalAirport="flight.arrivalAirport"
        :utc="displayUTC"
      />
      <FlightOverviewSchedule
        :claimTypeName="claimTypeName"
        :scheduledDepartureUTC="new Date(flight.scheduledDepartureUTC)"
        :scheduledArrivalUTC="new Date(flight.scheduledArrivalUTC)"
        :actualDepartureUTC="flight.actualDepartureUTC ? new Date(flight.actualDepartureUTC) : null"
        :inGateArrivalUTC="flight.inGateArrivalUTC ? new Date(flight.inGateArrivalUTC) : null"
        :departureAirport="flight.departureAirport"
        :arrivalAirport="flight.arrivalAirport"
        :utc="displayUTC"
        :monitoring-status="flight.monitoringStatus || null"
      />
    </div>
    
    <div v-if="reroutingProposals?.length" class="w-full pt-2"
    :class="claimTypeName === 'FLIGHT_CANCELLATION' || claimTypeName === 'PNR_SEGMENT_CANCELLATION' || claimTypeName === 'OVERBOOKING' ? 'border-l-2 border-red-600' : claimTypeName ? 'border-l-2 border-amber-500' : 'border-l-2 border-blue-500'">
      <div class="w-full p-2 mx-auto rounded-2xl">
        <Disclosure v-slot="{ open }" v-for="(rerouting, idx) in reroutingProposals" :key="rerouting.id" as="div" class="mb-2">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm text-left bg-white border rounded-lg shadow-md text-slate-900 hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75"
          >
            <span>Voir la proposition de réacheminement <span v-if="reroutingProposals.length > 1">#{{ idx + 1 }}</span></span>
            <ChevronUpIcon
              :class="open ? 'rotate-180 transform' : ''"
              class="w-5 h-5 text-slate-500"
            />
          </DisclosureButton>
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-left divide-y-8 divide-transparent">

              <div v-for="({ proposedSegment }, i) in rerouting.reroutingGroup.reroutings">
                <div>
                  <p class="flex py-2 font-bold" v-if="i > 0">
                    Escale numéro {{ i + 1 }}
                  </p>
                  <div class="overflow-hidden text-left bg-white border-y-2">
                    <FlightOverviewHeader 
                      :airline-id="proposedSegment.flight.airlineId"
                      :flight-id="proposedSegment.flight.id"
                      :airlineIconURL="proposedSegment.flight.airline.legalData?.iconURL"
                      :claimTypeName="null"
                      :flightIdentifier="proposedSegment.flight.flightIdentifier"
                      :airlineName="proposedSegment.flight.airline.name"
                    />
                  </div>
                  <FlightOverviewItinerary
                    :claimTypeName="null"
                    :scheduledDepartureUTC="new Date(proposedSegment.flight.scheduledDepartureUTC)"
                    :scheduledArrivalUTC="new Date(proposedSegment.flight.scheduledArrivalUTC)"
                    :departureAirport="proposedSegment.flight.departureAirport"
                    :arrivalAirport="proposedSegment.flight.arrivalAirport"
                    :utc="displayUTC"
                  />
                  <FlightOverviewSchedule
                    :claimTypeName="null"
                    :monitoring-status="proposedSegment.flight.monitoringStatus || null"
                    :scheduledDepartureUTC="new Date(proposedSegment.flight.scheduledDepartureUTC)"
                    :scheduledArrivalUTC="new Date(proposedSegment.flight.scheduledArrivalUTC)"
                    :actualDepartureUTC="proposedSegment.flight.actualDepartureUTC ? new Date(proposedSegment.flight.actualDepartureUTC) : null"
                    :inGateArrivalUTC="proposedSegment.flight.inGateArrivalUTC ? new Date(proposedSegment.flight.inGateArrivalUTC) : null"
                    :departureAirport="proposedSegment.flight.departureAirport"
                    :arrivalAirport="proposedSegment.flight.arrivalAirport"
                    :utc="displayUTC"
                  />
                </div>
              </div>

            </DisclosurePanel>
          </transition>
        </Disclosure>
      </div>
    </div>
    <div v-else-if="claimTypeName === 'FLIGHT_CANCELLATION' || claimTypeName === 'PNR_SEGMENT_CANCELLATION' || claimTypeName === 'OVERBOOKING'" class="w-full border-l-2 border-red-600">
      <div class="w-full p-2 mx-auto rounded-2xl">
        <Disclosure v-slot="{ open }" as="div" class="mb-2">
          <DisclosureButton
            class="flex justify-between w-full px-4 py-2 text-sm font-normal text-left border rounded-lg shadow cursor-default text-slate-900"
          >
            <p>Aucune proposition de réacheminement</p>
          </DisclosureButton>
        </Disclosure>
      </div>
    </div>
  </div>
</template>
